<template>
    <div>
        <DTable
            :items="terminations"
            :fields="fields"
            :sorter="isSortable"
            :sorter-value="sorterValue"
            class="table-responsive"
            @update:sorter-value="updateOrdering"
        >
            <template #brand_name="{item}">
                <td @click="addBrandToFilter(item.brand_id)">
                    <Link
                        v-if="userPermissions.includes('setting.brands')"
                        :href="$route('settings.brands.edit', {brand: item.brand_id})"
                    >
                        {{ item.brand_name ? item.brand_name : '' }}
                    </Link>
                    <span v-else>
                        {{ item.brand_name ? item.brand_name : '' }}
                    </span>
                </td>
            </template>
            <template #customer_id="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="$route('customers.customers.show', {customer: item.customer_id})">
                        <div v-show="item.customer_company">
                            {{ item.customer_company }}<br>
                        </div>
                        <span v-show="item.customer_first_name || item.customer_last_name">
                            {{ item.customer_first_name }} {{ item.customer_last_name }}</span>
                        ({{ item.customer_id }})
                    </Link>
                </td>
                <td v-else>
                    <div v-show="item.customer_company">
                        {{ item.customer_company }}<br>
                    </div>
                    <span v-show="item.customer_first_name || item.customer_last_name">
                        {{ item.customer_first_name }} {{ item.customer_last_name }}</span>
                    ({{ item.customer_id }})
                </td>
            </template>
            <template #type="{item}">
                <td>{{ item.type }}</td>
            </template>
            <template #product_name="{item}">
                <td v-if="userPermissions.includes('contract.view')">
                    <Link :href="$route('customers.customers.contracts.index', {customer: item.customer_id, contract: item.contract_id })">
                        <span v-if="item.type !== 'domain'">{{
                            item.product_name
                        }}</span>
                        <span v-else>{{ item.fulfillment_contract_config_value }}</span>
                        ({{ item.contract_id }})
                    </Link>
                </td>
                <td v-else>
                    <span v-if="item.type !== 'domain'">{{
                        item.product_name
                    }}</span>
                    <span v-else>{{ item.fulfillment_contract_config_value }}</span>
                    ({{ item.contract_id }})
                </td>
            </template>
            <template #suspended_at="{item}">
                <td>{{ setDateFormatEuropeBerlin(item.suspended_at) }}</td>
            </template>
            <template #completed_at="{item}">
                <td>{{ setDateFormatEuropeBerlin(item.completed_at) }}</td>
            </template>
            <template #date="{item}">
                <td>{{ setDateFormatEuropeBerlin(item.date) }}</td>
            </template>
            <template #termination_entry_date="{item}">
                <td>{{ setDateFormatEuropeBerlin(item.termination_entry_date) }}</td>
            </template>
            <template #termination_reason_name="{item}">
                <td>{{ item.termination_reason_name }}</td>
            </template>
            <template #comment="{item}">
                <td>{{ item.comment ?? '' }}</td>
            </template>
            <template #additionalData="{item}">
                <td class="additional-data">
                    <div v-if="getAuthCode(item)">
                        {{ __('contracts.terminations.authcode') }}: {{ getAuthCode(item) }}
                    </div>
                    <div v-if="hasDuplicates(item)">
                        <Link
                            class="text-danger"
                            :href="viewDuplicatesLink(item)"
                        >
                            <strong>{{ __('contracts.terminations.view_duplicates') }}</strong>
                        </Link>
                    </div>
                    <div v-if="item.needs_check">
                        {{ __('contracts.terminations.needs_check') }}
                    </div>
                </td>
            </template>
            <template #action="{item}">
                <td class="actions">
                    <DButton
                        v-if="!item.manual_termination_disallowed"
                        mini
                        :label="__('customers.contracts.cancel_terminations')"
                        @click="showCancelTerminationConfirm(item)"
                    />
                    <DButton
                        v-if="showMarkAsCompleteAction(item)"
                        mini
                        :label="__('contracts.terminations.mark_termination_as_complete')"
                        @click="showCompleteTerminationConfirm(item)"
                    />
                    <DButton
                        v-if="item.needs_check"
                        mini
                        :label="__('contracts.terminations.remove_check')"
                        @click="removeCheck(item)"
                    />
                </td>
            </template>
        </DTable>
        <DPagination
            v-show="pages > 1"
            align="center"
            :active-page="page"
            :pages="pages"
            @update:activePage="changePage"
        />
        <DModal
            v-model:show="showConfirmationModal"
            :title="__('customers.terminations.confirm_title')"
            :message="__('customers.terminations.confirm_message')"
            is-confirm
            is-error
            @confirm="cancelTermination"
            @close="showConfirmationModal = false"
        />
        <DModal
            v-model:show="showConfirmTerminationCompleteModal"
            :title="__('contracts.terminations.confirm_complete_termination')"
            :message="__('contracts.terminations.confirm_complete_message')"
            is-confirm
            @confirm="markTerminationAsComplete"
            @close="showConfirmTerminationCompleteModal = false"
        />
    </div>
</template>

<script>
export default {
    name: 'TerminationList',
    props: {
        terminations: {
            type: Array,
            default: () => []
        },
        page: {
            type: Number,
            default: 1
        },
        pages: {
            type: Number,
            default: 0
        },
        withSuspendedAt: {
            type: Boolean,
            default: false
        },
        withCompletedAt: {
            type: Boolean,
            default: false
        },
        withoutActionColumn: {
            type: Boolean,
            default: false
        },
        orderValues: {
            type: Object,
            default: () => {
                return {}
            }
        }
    },
    emits: ['pageChanged', 'cancelTermination', 'markAsCompleted', 'addBrandToFilter', 'removeCheck', 'updateOrderValues'],
    data () {
        const fields = [
            { key: 'id', sorter: true, label: this.__('contracts.terminations.id') },
            { key: 'brand_name', sorter: true, label: this.__('contracts.terminations.brand') },
            { key: 'customer_id', sorter: true, label: this.__('contracts.terminations.customer') },
            { key: 'type', sorter: true, label: this.__('contracts.terminations.type') },
            { key: 'product_name', sorter: true, label: this.__('contracts.terminations.product') },
            { key: 'date', sorter: true, label: this.__('contracts.terminations.terminated_to') },
            { key: 'termination_entry_date', sorter: true, label: this.__('contracts.terminations.terminated_at') },
            { key: 'status', sorter: true, label: this.__('contracts.terminations.status') },
            { key: 'contract_status', sorter: true, label: this.__('contracts.terminations.contract_status') },
            { key: 'source', sorter: true, label: this.__('contracts.terminations.source') },
            { key: 'termination_reason_name', sorter: true, label: this.__('contracts.terminations.reason') },
            { key: 'comment', sorter: true, label: this.__('contracts.terminations.comment') },
            { key: 'additionalData', sorter: false, label: this.__('contracts.terminations.additional_data') }

        ]
        if (this.withCompletedAt) {
            fields.splice(5, 0, { key: 'completed_at', label: this.__('contracts.terminations.completed_at') })
        }

        if (this.withSuspendedAt) {
            fields.splice(5, 0, { key: 'suspended_at', label: this.__('contracts.terminations.suspended_at') })
        }

        if (!this.withoutActionColumn) {
            fields.push({ key: 'action', label: '', sorter: false })
        }
        return {
            showConfirmationModal: false,
            toCancelTermination: null,
            toCompleteTermination: null,
            showConfirmTerminationCompleteModal: false,
            fields
        }
    },
    computed: {
        sorterValue () {
            return {
                column: this.orderValues.orderBy,
                asc: !!this.orderValues.orderByAsc
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        getAuthCode (item) {
            return item.auth_code
        },
        changePage (page) {
            this.$emit('pageChanged', page)
        },
        cancelTermination () {
            this.$emit('cancelTermination', this.toCancelTermination.id)
        },
        showCancelTerminationConfirm (item) {
            this.toCancelTermination = item
            this.showConfirmationModal = true
        },
        showCompleteTerminationConfirm (item) {
            this.toCompleteTermination = item
            this.showConfirmTerminationCompleteModal = true
        },
        markTerminationAsComplete () {
            this.$emit('markAsCompleted', this.toCompleteTermination.id)
        },
        addBrandToFilter (brandId) {
            this.$emit('addBrandToFilter', brandId)
        },
        removeCheck (item) {
            this.$emit('removeCheck', item)
        },
        hasDuplicates (item) {
            return item.hasDuplicates
        },
        viewDuplicatesLink (item) {
            return this.$route('contracts.duplicates', {
                search: item.external_id
            })
        },
        updateOrdering (event) {
            const orderByAsc = event.asc ? 1 : 0
            this.$emit('updateOrderValues', event.column, orderByAsc)
        },
        showMarkAsCompleteAction (item) {
            return (!(new Date() < (new Date(item.date)) && item.status === 'open'))
        }
    }
}
</script>

<style lang="scss">
.additional-data {
    display: grid;
    grid-gap: 10px;
}
.actions {
    display: grid;
    grid-gap: 10px;
}
</style>
